// CRxxx: Step Error Details Component for transparent error debugging
// CR159: Enhanced to show specialized diagnostics for NO_MERCHANT_SOURCES errors
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Copy, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MerchantSourcesDiagnostics } from "./MerchantSourcesDiagnostics";

interface StepDebugInfo {
  http_status?: number;
  http_status_text?: string;
  error_code?: string;
  error_message?: string;
  response_body?: string;
  request_payload?: Record<string, unknown>;
  invocation_id?: string;
  step_id?: string;
  timestamp?: string;
  // CR159: Enhanced NO_MERCHANT_SOURCES fields
  merchant_ids?: string[];
  scoped_merchant_keys?: string[];
  source_type_requested?: string;
  source_type_aliases_checked?: string[];
  filters_applied?: Record<string, unknown>;
  per_merchant_source_counts?: Record<string, number>;
  filtered_out_reasons?: Record<string, number>;
  suggested_fix?: string;
  all_sources_found?: number;
  schedule_run_id?: string | null;
}

interface StepErrorDetailsProps {
  errorMessage: string | null;
  configJson?: Record<string, unknown> | null;
  stepId?: string;
}

export function StepErrorDetails({ errorMessage, configJson, stepId }: StepErrorDetailsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  if (!errorMessage) return null;

  // Extract debug info from config_json
  const debug = configJson?.debug as StepDebugInfo | undefined;
  const hasDebugInfo = debug && (debug.http_status || debug.error_code || debug.response_body || debug.per_merchant_source_counts);

  // CR159: Check if this is a NO_MERCHANT_SOURCES error for specialized rendering
  const isNoMerchantSourcesError = debug?.error_code === "NO_MERCHANT_SOURCES";
  
  // CR162: Check for MISSING_PROMPT_PACK error
  const isMissingPromptPackError = debug?.error_code === "MISSING_PROMPT_PACK";

  const handleCopy = async () => {
    const debugData = {
      error_message: errorMessage,
      step_id: stepId,
      ...debug,
    };
    
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
      setCopied(true);
      toast({ title: "Copied", description: "Error details copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  // HTTP status badge color
  const getStatusBadgeVariant = (status?: number): "default" | "secondary" | "destructive" | "outline" => {
    if (!status) return "secondary";
    if (status >= 500) return "destructive";
    if (status >= 400) return "outline";
    return "default";
  };

  // CR159: Render specialized component for NO_MERCHANT_SOURCES errors
  if (isNoMerchantSourcesError && debug) {
    return (
      <div className="space-y-2">
        <MerchantSourcesDiagnostics debug={debug} stepId={stepId} />
      </div>
    );
  }
  
  // CR162: Render specialized view for MISSING_PROMPT_PACK errors
  if (isMissingPromptPackError && debug) {
    return (
      <div className="space-y-2">
        <div className="p-3 bg-warning/10 text-warning-foreground rounded border border-warning/20">
          <div className="flex items-start gap-2">
            <span className="font-medium">⚠️ Missing Prompt Pack</span>
          </div>
          <p className="mt-2 text-sm">
            No prompt pack is configured for WEB collection. Prompt packs are required for Website scans.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            <strong>Fix:</strong> {debug.suggested_fix || "Configure a prompt pack in the Scheduler source config or create a new batch run with a prompt pack selected."}
          </p>
          <div className="mt-3 flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 px-2 text-xs"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copy Diagnostics
                </>
              )}
            </Button>
          </div>
          {debug.schedule_run_id && (
            <p className="mt-2 text-xs text-muted-foreground">
              Schedule Run: <code className="bg-muted px-1 rounded">{debug.schedule_run_id}</code>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Main error message */}
      <div className="p-2 bg-destructive/10 text-destructive rounded text-sm">
        <div className="flex items-start justify-between gap-2">
          <span className="flex-1">{errorMessage}</span>
          {hasDebugInfo && debug?.http_status && (
            <Badge variant={getStatusBadgeVariant(debug.http_status)} className="shrink-0">
              HTTP {debug.http_status}
            </Badge>
          )}
        </div>
      </div>

      {/* Expandable debug details */}
      {hasDebugInfo && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1 h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
              {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              View Error Details
              {debug.error_code && (
                <Badge variant="outline" className="ml-1 text-xs px-1 py-0">
                  {debug.error_code}
                </Badge>
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 p-3 bg-muted/50 rounded-md text-xs font-mono space-y-2 border">
              {/* Copy button */}
              <div className="flex justify-end">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 mr-1" />
                      Copy All
                    </>
                  )}
                </Button>
              </div>

              {/* Debug fields */}
              <div className="space-y-1.5">
                {debug.http_status && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-28 shrink-0">HTTP Status:</span>
                    <span>{debug.http_status} {debug.http_status_text || ''}</span>
                  </div>
                )}
                {debug.error_code && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-28 shrink-0">Error Code:</span>
                    <span className="text-destructive">{debug.error_code}</span>
                  </div>
                )}
                {stepId && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-28 shrink-0">Step ID:</span>
                    <span className="break-all">{stepId}</span>
                  </div>
                )}
                {debug.invocation_id && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-28 shrink-0">Invocation ID:</span>
                    <span className="break-all">{debug.invocation_id}</span>
                  </div>
                )}
                {debug.timestamp && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-28 shrink-0">Timestamp:</span>
                    <span>{debug.timestamp}</span>
                  </div>
                )}
              </div>

              {/* Response body */}
              {debug.response_body && (
                <div className="mt-2">
                  <span className="text-muted-foreground">Response Body:</span>
                  <pre className="mt-1 p-2 bg-background rounded border text-xs overflow-x-auto max-h-48 whitespace-pre-wrap break-all">
                    {typeof debug.response_body === 'string' 
                      ? debug.response_body 
                      : JSON.stringify(debug.response_body, null, 2)}
                  </pre>
                </div>
              )}

              {/* Request payload */}
              {debug.request_payload && (
                <div className="mt-2">
                  <span className="text-muted-foreground">Request Payload:</span>
                  <pre className="mt-1 p-2 bg-background rounded border text-xs overflow-x-auto max-h-32">
                    {JSON.stringify(debug.request_payload, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
